import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/NewDashboard';
import AIModelDistribution from './pages/AIModelDistribution';
import Teams from './pages/Teams';
import Users from './pages/Users';
import DeveloperDetails from './pages/DeveloperDetails';
import Admin from './pages/Admin';
import MaintenancePage from './pages/MaintenancePage';

import DataImport from './pages/DataImport';
import Scheduler from './pages/Scheduler';
import SpendImport from './pages/SpendImport';
import UBBReport from './pages/UBBReport';
import LoadingSpinner from './components/LoadingSpinner';
import api from './utils/api';

// Maintenance-aware Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      if (user) {
        try {
          const response = await api.get('/maintenance/status');
          setMaintenanceStatus(response.data);
        } catch (error) {
          console.error('Error checking maintenance status:', error);
          // If we can't check maintenance status, assume it's not in maintenance mode
          setMaintenanceStatus({ isMaintenanceMode: false });
        } finally {
          setMaintenanceLoading(false);
        }
      } else {
        setMaintenanceLoading(false);
      }
    };

    checkMaintenanceMode();
  }, [user]);

  if (loading || maintenanceLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check maintenance mode for non-admin users
  if (maintenanceStatus?.isMaintenanceMode && user.role !== 'admin') {
    return <MaintenancePage maintenanceMessage={maintenanceStatus.message} />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  return (
    <Router>
      <div className="min-h-screen bg-secondary-50">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          
          {/* Auth Callback Route (public but handles tokens) */}
          <Route
            path="/auth/callback"
            element={<AuthCallback />}
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="ai-model-distribution" element={<AIModelDistribution />} />
            <Route path="teams" element={<Teams />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<DeveloperDetails />} />
            <Route path="ubb-report" element={<UBBReport />} />

            <Route
              path="data-import"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <DataImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="scheduler"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <Scheduler />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="spend-import"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <SpendImport />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        {/* Toast notifications */}
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#ffffff',
              color: '#1e293b',
              border: '1px solid #e2e8f0',
              borderRadius: '0.75rem',
              boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
            },
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#ffffff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 